import { useEffect, useMemo, useState, useRef } from 'react';
import {
  Heading,
  Text,
  Icon,
  Center,
  Spinner,
  Image,
  VStack,
  Box,
  Spacer,
  Flex,
  chakra,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';

import PageContainer from '~/containers/PageContainer';
import ContentContainer from '~/containers/Content';
import Content from '~/containers/Content';

import SectionContainer from '~/containers/SectionContainer';
import BackwardBar from '~/components/BackwardBar';
import EmptySection from '~/components/EmptySection';
import GoTopButton from '~/components/GoTopButton';
import FooterCartStatusBar from '~/components/FooterCartStatusBar';
import FooterLoginSection from '~/components/FooterLoginSection';
import InfinityItemList from '~/components/InfinityItemList';

import authAtom from '~/recoil/atom/auth';
import displayStoresSelector from '~/recoil/selectors/displayStores';
import filterMealSearchFormAtom from '~/recoil/atom/filterMealSearchForm';
import currentPickMealPointSelector from '~/recoil/selectors/currentPickMealPoint';
import { useRecoilValue } from 'recoil';

import useStoreList from '~/hooks/useStoreList';
import useSearchItem from '~/hooks/useSearchItem';
import useCartId from '~/hooks/useCartId';

import { FetchingDataStatus } from '~/types/utilities';
import { CartType } from '~/types/cart';

import fp from 'lodash/fp';

import NoSearchResult from '~/assets/images/search.png';

import { IoRestaurantOutline } from 'react-icons/io5';
export default function SearchItem() {
  const navigate = useNavigate();
  const { groupId = '' } = useParams();

  const [showHeader, setShowHeader] = useState<boolean>(true);
  const auth = useRecoilValue(authAtom);
  const displayStores = useRecoilValue(displayStoresSelector);
  const filterMealSearchForm = useRecoilValue(filterMealSearchFormAtom);
  const currentPickMealPoint = useRecoilValue(currentPickMealPointSelector);
  const { cartId, cartType } = useCartId();

  const itemListRef = useRef<any>();

  const availableStores = useMemo(
    () => currentPickMealPoint.stores.filter((storeId) => displayStores.includes(storeId)),
    [displayStores, currentPickMealPoint.stores],
  );
  const searchStores = useMemo(() => {
    if (filterMealSearchForm.storeIds.length) {
      return filterMealSearchForm.storeIds;
    }
    return availableStores;
  }, [filterMealSearchForm.storeIds, availableStores]);

  const { storeList } = useStoreList(availableStores);
  const {
    searchItems,
    searchItemCount,
    fetchingStatus: fetchingStatusOfItem,
    handleLoadNextPage,
  } = useSearchItem({ ...filterMealSearchForm, storeIds: searchStores, itemLimit: 100 });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const handleNavigateToItem = (storeId: string, itemId: string) =>
    navigate(
      groupId
        ? `/group/${groupId}/store/${storeId}/item/${itemId}/add`
        : `/store/${storeId}/item/${itemId}/add`,
      { state: { previous: window.location.pathname } },
    );

  const handleSetDisplayHeader = (val: boolean) => {
    if (val === showHeader) {
      return;
    }
    setShowHeader(val);
  };

  const handleSetItemListRef = (list: any) => {
    itemListRef.current = list;
  };

  const searchText = filterMealSearchForm.searchText
    ? `「${filterMealSearchForm.searchText}」`
    : '';
  const searchStoresText = filterMealSearchForm.storeIds.length
    ? `「${filterMealSearchForm.storeIds
        .map((storeId) => storeList.find(({ objectID }) => objectID === storeId)?.name || '')
        .filter((name) => name)
        .join(',')}」`
    : '「全部餐廳」';
  const searchTagsText = filterMealSearchForm.tags.length
    ? `「${filterMealSearchForm.tags.join(',')}」`
    : '「全部素食分類」';

  const searchList = searchText
    ? [searchText, searchStoresText, searchTagsText]
    : [searchStoresText, searchTagsText];

  return (
    <PageContainer
      hideHeader
      hideNavBar
      afterHeaderComponent={
        <>
          <BackwardBar
            title="返回"
            onNavigate={() => navigate(groupId ? `group/${groupId}` : '/')}
          />
          <Heading
            py={3}
            size="md"
            bgColor="white"
            textAlign="center"
            borderBottomWidth="1px"
            borderColor="primary"
          >
            餐點搜尋結果
          </Heading>
        </>
      }
    >
      <Box bgColor="white" my={-4} py={4}>
        <ContentContainer flexGrow={1} minH={window.innerHeight - 70} my={0} px={4}>
          {showHeader ? (
            <>
              <Text fontSize="sm" my={4}>
                搜尋
                <chakra.span fontSize="lg">{searchList.join('、')}</chakra.span>
                的結果
              </Text>
              <SectionContainer
                title="餐點"
                icon={<Icon as={IoRestaurantOutline} color="secondary" boxSize="1.25rem" />}
              />
              <Text fontSize="sm" color="gray.500" mb={4}>
                符合搜尋結果: {searchItemCount} 個
              </Text>
            </>
          ) : (
            <></>
          )}
          {fp.cond([
            [
              fp.where({
                itemsLength: fp.eq(0),
                state: fp.eq(FetchingDataStatus.LOADING),
              }),
              () => (
                <Center>
                  <Spinner size="lg" />
                </Center>
              ),
            ],
            [
              fp.where({
                state: fp.eq(FetchingDataStatus.FAIL),
              }),
              () => <EmptySection content="獲取資料發生錯誤請重試" />,
            ],
            [
              fp.where({
                state: fp.eq(FetchingDataStatus.SUCCESS),
                itemsLength: fp.eq(0),
              }),
              () => (
                <VStack spacing={6} py={5} alignItems="center">
                  <Text color="fontColor" fontSize="sm">
                    查無結果
                  </Text>
                  <Image src={NoSearchResult} w={180} />
                </VStack>
              ),
            ],
            [
              fp.where({
                itemsLength: fp.lt(0),
              }),
              () => (
                <InfinityItemList
                  fetchingStatusOfItem={fetchingStatusOfItem}
                  itemCount={searchItemCount}
                  items={searchItems}
                  onLoadNextPage={handleLoadNextPage}
                  onNavigateToItem={handleNavigateToItem}
                  onSetHeaderDisplay={handleSetDisplayHeader}
                  onSetItemListRef={handleSetItemListRef}
                />
              ),
            ],
            [fp.T, fp.always(null)],
          ])({
            state: fetchingStatusOfItem,
            itemsLength: searchItemCount,
          })}
        </ContentContainer>
        <Box position="sticky" bottom={0}>
          <Box px={4}>
            <Content py={2}>
              <Flex>
                <Spacer />
                <GoTopButton onClick={() => itemListRef.current?.scrollTo(0)} />
              </Flex>
            </Content>
          </Box>

          <>
            {fp.eq(cartType, CartType.CART_PERSONAL) && !auth ? (
              <FooterLoginSection />
            ) : (
              <FooterCartStatusBar
                onNavigate={() =>
                  navigate(fp.eq(cartType, CartType.CART_GROUP) ? `/group/${cartId}/cart` : '/cart')
                }
              />
            )}
          </>
        </Box>
      </Box>
    </PageContainer>
  );
}
